import React, { useState, useMemo, useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';
import { Avatar, Button, Space, Tag, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  LinkOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
// API
import { MY_CB_TALENT_LIST } from 'api/cbTalent/queries';
import { CREATE_TALENT_INVITE_LINK_ALL_DEALS } from 'api/campaignV2/invite/mutations';
import getEnvVariables from 'api/env';
// Types
import {
  MyCbTalentList,
  MyCbTalentListVariables,
  MyCbTalentList_myCbTalentList_entities,
} from 'api/cbTalent/types/MyCbTalentList';
import {
  CreateTalentInviteLinkAllDeals,
  CreateTalentInviteLinkAllDealsVariables,
} from 'api/campaignV2/invite/types/CreateTalentInviteLinkAllDeals';
// Constants
import { BRANDS } from 'constants/routes';
// UI
import Table from 'ui/Table';
import { successNotification, errorNotification } from 'ui/Notification';
// Components
import AddTalentModal from './components/AddTalentModal';

const { Title } = Typography;

type TalentEntity = MyCbTalentList_myCbTalentList_entities;

const formatDate = (value?: string | null): string =>
  value ? dayjs(value).format('MM/DD/YYYY') : '-';

const formatRate = (rate?: number | null): string =>
  typeof rate === 'number' ? `$${rate.toLocaleString()}` : '-';

const BrandTalentCrmTable: React.FC = () => {
  const history = useHistory();
  const { agencyId } = useParams<{ agencyId: string }>();

  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isAddModalVisible, setIsAddModalVisible] = useState<boolean>(false);

  const variables = useMemo<MyCbTalentListVariables>(
    () => ({
      input: {
        ownerId: agencyId,
        limit: pageSize,
        offset: (currentPage > 1 ? currentPage - 1 : 0) * pageSize,
      },
    }),
    [agencyId, pageSize, currentPage]
  );

  const { data, loading, error, refetch } = useQuery<
    MyCbTalentList,
    MyCbTalentListVariables
  >(MY_CB_TALENT_LIST, {
    variables,
    fetchPolicy: 'cache-and-network',
    skip: !agencyId,
  });

  const [createInviteLink, { loading: creatingInviteLink }] = useMutation<
    CreateTalentInviteLinkAllDeals,
    CreateTalentInviteLinkAllDealsVariables
  >(CREATE_TALENT_INVITE_LINK_ALL_DEALS);

  const handleBack = useCallback(() => {
    history.push(BRANDS);
  }, [history]);

  const handleCopyInviteLink = useCallback(async () => {
    let code: string | undefined;
    try {
      const { data: inviteData } = await createInviteLink({
        variables: { input: { ownerId: agencyId } },
      });

      code = inviteData?.createTalentInviteLinkAllDeals?.code;
      if (!code) {
        throw new Error('No invite link code returned');
      }
    } catch {
      errorNotification('Failed to create invite link');
      return;
    }

    try {
      await navigator.clipboard.writeText(
        `${getEnvVariables.REACT_APP_WEB_PORTAL_URL}/campaign/invite/${code}`
      );
      successNotification('Invite link copied to clipboard');
    } catch {
      errorNotification(
        'Invite link created but could not be copied to clipboard'
      );
    }
  }, [createInviteLink, agencyId]);

  const handleOpenAdd = useCallback(() => {
    setIsAddModalVisible(true);
  }, []);

  const handleCloseAdd = useCallback(() => {
    setIsAddModalVisible(false);
  }, []);

  const handleAddSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const columns = useMemo(
    () => [
      {
        title: 'Talent',
        dataIndex: 'user',
        key: 'user',
        width: 240,
        render: (_: unknown, record: TalentEntity) => {
          const user = record.user;
          if (!user) {
            return '-';
          }
          const fullName = [user.firstName, user.lastName]
            .filter(Boolean)
            .join(' ');
          return (
            <Space>
              <Avatar
                src={user.avatarUrl || undefined}
                icon={<UserOutlined />}
              />
              <div>
                <div>{fullName || '-'}</div>
                {user.email && (
                  <Typography.Text type="secondary">
                    {user.email}
                  </Typography.Text>
                )}
              </div>
            </Space>
          );
        },
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 140,
        render: (value: string | null | undefined) => value || '-',
      },
      {
        title: 'Source',
        dataIndex: 'source',
        key: 'source',
        width: 140,
        render: (value: string | null | undefined) => value || '-',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (value: string | null | undefined) =>
          value ? <Tag>{value}</Tag> : '-',
      },
      {
        title: 'Sports',
        dataIndex: ['user', 'sports'],
        key: 'sports',
        width: 200,
        render: (_: unknown, record: TalentEntity) => {
          if (!record.user?.sports?.length) {
            return '-';
          }
          return (
            <Space wrap size={4}>
              {record.user.sports.map((sport) => (
                <Tag key={sport.id}>{sport.name}</Tag>
              ))}
            </Space>
          );
        },
      },
      {
        title: 'Categories',
        dataIndex: ['user', 'influencerCategories'],
        key: 'influencerCategories',
        width: 220,
        render: (_: unknown, record: TalentEntity) => {
          if (!record.user?.influencerCategories?.length) {
            return '-';
          }
          return (
            <Space wrap size={4}>
              {record.user.influencerCategories.map((category) => (
                <Tag key={category.id}>{category.name}</Tag>
              ))}
            </Space>
          );
        },
      },
      {
        title: 'Last Rate',
        dataIndex: 'lastRate',
        key: 'lastRate',
        width: 120,
        render: (value: number | null | undefined) => formatRate(value),
      },
      {
        title: 'First Worked',
        dataIndex: 'firstWorkedAt',
        key: 'firstWorkedAt',
        width: 140,
        render: (value: string | null | undefined) => formatDate(value),
      },
      {
        title: 'Last Worked',
        dataIndex: 'lastWorkedAt',
        key: 'lastWorkedAt',
        width: 140,
        render: (value: string | null | undefined) => formatDate(value),
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 140,
        render: (value: string | null | undefined) => formatDate(value),
      },
    ],
    []
  );

  if (error) {
    return <div>Error loading talents: {error.message}</div>;
  }

  return (
    <div>
      <Space
        align="center"
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            Back to Brands
          </Button>
          <Title level={2} style={{ margin: 0 }}>
            Talent CRM
          </Title>
        </Space>
        <Space>
          <Button
            icon={<LinkOutlined />}
            loading={creatingInviteLink}
            disabled={!agencyId}
            onClick={handleCopyInviteLink}
          >
            Copy Invite Link
          </Button>
          <Button type="primary" onClick={handleOpenAdd}>
            Add Talent
          </Button>
        </Space>
      </Space>

      <Table<TalentEntity, never>
        columns={columns}
        data={data?.myCbTalentList?.entities || []}
        loading={loading}
        scroll={{ x: 1500 }}
        total={data?.myCbTalentList?.total}
        currentPage={currentPage}
        pageSize={pageSize}
        setCurrentPage={setCurrentPage}
        setPageSize={setPageSize}
      />

      <AddTalentModal
        agencyId={agencyId}
        isVisible={isAddModalVisible}
        onClose={handleCloseAdd}
        onSuccess={handleAddSuccess}
      />
    </div>
  );
};

export default BrandTalentCrmTable;
